/**
 * 🎯 Editing Guard System
 * 
 * Prevents programmatic overwrites of fields that are currently being edited
 * Tracks editing state by field path to ensure user input isn't disrupted
 */

class EditingGuard {
  constructor() {
    this.editingFields = new Map();
    this.preventedWrites = 0;
    this.isActive = false;
  }

  /**
   * Initialize the guard system (call once per component mount)
   */
  activate() {
    this.isActive = true;
    console.log('📱 Phone field guard active.');
  }

  /**
   * Generate a unique key for a field path
   */
  getFieldKey(fieldPath) {
    if (Array.isArray(fieldPath)) {
      return fieldPath.join('.');
    }
    return String(fieldPath);
  }

  /**
   * Mark a field as being edited
   */
  setEditing(fieldPath, isEditing = true) {
    const key = this.getFieldKey(fieldPath);
    if (isEditing) {
      this.editingFields.set(key, true);
      console.log(`🎯 Phone field focus: ${key}`);
    } else {
      this.editingFields.delete(key);
      console.log(`🎯 Phone field blur: ${key}`);
    }
  }

  /**
   * Check if a field is currently being edited
   */
  isEditing(fieldPath) {
    const key = this.getFieldKey(fieldPath);
    return this.editingFields.has(key);
  }

  /**
   * Filter out fields that are currently being edited from form updates
   * Returns a new object with editing fields removed
   */
  filterEditingFields(formValues) {
    if (!this.isActive || this.editingFields.size === 0) {
      return formValues;
    }

    const filtered = { ...formValues };
    let removedFields = [];

    // Check nested structures for phone fields
    this.editingFields.forEach((_, fieldKey) => {
      if (this.shouldSkipField(fieldKey, filtered)) {
        this.removeFieldFromObject(filtered, fieldKey);
        removedFields.push(fieldKey);
      }
    });

    if (removedFields.length > 0) {
      this.preventedWrites++;
      console.warn(`⚠️ Programmatic write skipped for editing fields: ${removedFields.join(', ')} (total prevented: ${this.preventedWrites})`);
    }

    return filtered;
  }

  /**
   * Check if a field should be skipped based on its path
   */
  shouldSkipField(fieldKey, formValues) {
    const parts = fieldKey.split('.');
    let current = formValues;

    // Navigate to the field location
    for (let i = 0; i < parts.length - 1; i++) {
      if (current && typeof current === 'object') {
        current = current[parts[i]];
      } else {
        return false; // Path doesn't exist in form values
      }
    }

    // Check if the final field exists and has a value
    const finalKey = parts[parts.length - 1];
    return current && current.hasOwnProperty(finalKey);
  }

  /**
   * Remove a field from a nested object by path
   */
  removeFieldFromObject(obj, fieldKey) {
    const parts = fieldKey.split('.');
    let current = obj;

    // Navigate to parent object
    for (let i = 0; i < parts.length - 1; i++) {
      if (current && typeof current === 'object') {
        current = current[parts[i]];
      } else {
        return; // Path doesn't exist
      }
    }

    // Remove the final field
    const finalKey = parts[parts.length - 1];
    if (current && typeof current === 'object') {
      delete current[finalKey];
    }
  }

  /**
   * Get stats for debugging
   */
  getStats() {
    return {
      isActive: this.isActive,
      editingFieldCount: this.editingFields.size,
      preventedWrites: this.preventedWrites,
      editingFields: Array.from(this.editingFields.keys())
    };
  }

  /**
   * Reset the guard (useful for cleanup)
   */
  reset() {
    this.editingFields.clear();
    this.preventedWrites = 0;
    this.isActive = false;
  }
}

// Create a singleton instance
export const editingGuard = new EditingGuard();

/**
 * Hook for React components to use the editing guard
 */
export function useEditingGuard() {
  return {
    setEditing: (fieldPath, isEditing) => editingGuard.setEditing(fieldPath, isEditing),
    isEditing: (fieldPath) => editingGuard.isEditing(fieldPath),
    filterEditingFields: (formValues) => editingGuard.filterEditingFields(formValues),
    activate: () => editingGuard.activate(),
    getStats: () => editingGuard.getStats(),
    reset: () => editingGuard.reset()
  };
}

export default editingGuard;

